import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import qaData from "../data/qaData";
import "./QAChatBot.css";
import {
  FaComments,
  FaTimes,
  FaPaperPlane,
  FaRedo,
  FaWhatsapp,
  FaExternalLinkAlt,
  FaHeadset,
} from "react-icons/fa";

const INITIAL_MESSAGE = {
  id: "welcome",
  sender: "bot",
  text: "Hello! Welcome to CIAP Maritime & Engineering Solutions. ⚓ How can we assist you today? Select a popular topic below or type your question.",
  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  action: null,
};

const QAChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Clean up any pending timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      // Auto-focus input when opened
      const focusTimer = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(focusTimer);
    }
  }, [messages, isTyping, isOpen]);

  // Answer matching engine
  const findAnswer = (query) => {
    const qLower = query.toLowerCase().trim();

    // 1. Exact or partial match on question
    let match = qaData.find((item) =>
      item.question.toLowerCase().includes(qLower) || qLower.includes(item.question.toLowerCase())
    );

    // 2. Keyword match
    if (!match) {
      match = qaData.find((item) =>
        item.keywords?.some((kw) => qLower.includes(kw.toLowerCase()))
      );
    }

    // 3. Match within answer text
    if (!match) {
      match = qaData.find((item) =>
        item.answer.toLowerCase().includes(qLower)
      );
    }

    if (match) {
      return {
        text: match.answer,
        action: match.action || null,
      };
    }

    // Fallback response for unhandled inquiries
    return {
      text: "Thank you for your inquiry! For specialized technical scopes, vessel attendances, or custom quotes, please connect directly with our 24/7 technical operations desk.",
      action: {
        text: "WhatsApp 24/7 Live Desk",
        link: "https://wa.me/94714820101",
        isExternal: true,
        isWhatsApp: true,
      },
    };
  };

  const handleSendMessage = (textToSend) => {
    const query = textToSend || inputText;
    if (!query.trim()) return;

    const timeString = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    // 1. Add user message
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: query.trim(),
      time: timeString,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText("");
    setIsTyping(true);

    // 2. Simulate natural response timing (450ms)
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      const response = findAnswer(query);
      const botMsg = {
        id: Date.now() + 1,
        sender: "bot",
        text: response.text,
        action: response.action,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 450);
  };

  const handleChipClick = (item) => {
    handleSendMessage(item.question);
  };

  const handleResetChat = () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setMessages([
      {
        ...INITIAL_MESSAGE,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setIsTyping(false);
  };

  return (
    <>
      {/* FLOATING LAUNCHER BUTTON */}
      <button
        className="qa-floating-launcher"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close Assistant" : "Open CIAP Assistant"}
        title={isOpen ? "Close Assistant" : "CIAP Technical Assistant"}
      >
        <span className="qa-launcher-status-dot"></span>
        {isOpen ? <FaTimes /> : <FaComments />}
        <span className="qa-launcher-tooltip">24/7 Technical Assistant</span>
      </button>

      {/* CHAT WINDOW */}
      {isOpen && (
        <div className="qa-chat-window" role="dialog" aria-label="CIAP Chatbot">
          {/* Header */}
          <div className="qa-window-header">
            <div className="qa-header-profile">
              <div className="qa-header-avatar">
                <img src="/images/ciap-logo.png" alt="CIAP" />
              </div>
              <div className="qa-header-title-box">
                <h4>CIAP Maritime Assistant</h4>
                <span className="qa-header-status">
                  <span className="qa-status-indicator"></span>
                  Online • 24/7 Ready
                </span>
              </div>
            </div>

            <div className="qa-header-actions">
              <button
                className="qa-header-btn"
                onClick={handleResetChat}
                title="Restart Chat"
                aria-label="Restart Conversation"
              >
                <FaRedo />
              </button>
              <button
                className="qa-header-btn"
                onClick={() => setIsOpen(false)}
                title="Close Window"
                aria-label="Close Assistant"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Messages Stream */}
          <div className="qa-chat-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`qa-message-row ${msg.sender}`}>
                <div className="qa-msg-avatar">
                  {msg.sender === "user" ? (
                    <img src="/images/user-icon.png" alt="You" />
                  ) : (
                    <img src="/images/ciap-logo.png" alt="CIAP" />
                  )}
                </div>

                <div className="qa-msg-bubble">
                  <div>{msg.text}</div>

                  {/* Rich action button if attached */}
                  {msg.action && (
                    <div className="qa-msg-actions">
                      {msg.action.isExternal ? (
                        <a
                          href={msg.action.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`qa-action-pill ${msg.action.isWhatsApp ? "whatsapp" : "primary"}`}
                        >
                          {msg.action.isWhatsApp ? <FaWhatsapp /> : <FaExternalLinkAlt />}
                          <span>{msg.action.text}</span>
                        </a>
                      ) : (
                        <Link
                          to={msg.action.link}
                          className="qa-action-pill primary"
                          onClick={() => setIsOpen(false)}
                        >
                          <span>{msg.action.text}</span>
                          <FaExternalLinkAlt size={10} />
                        </Link>
                      )}
                    </div>
                  )}

                  <span className="qa-msg-time">{msg.time}</span>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="qa-message-row bot">
                <div className="qa-msg-avatar">
                  <img src="/images/ciap-logo.png" alt="CIAP" />
                </div>
                <div className="qa-typing-bubble">
                  <span className="qa-typing-dot"></span>
                  <span className="qa-typing-dot"></span>
                  <span className="qa-typing-dot"></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions Section */}
          <div className="qa-quick-chips-box">
            <div className="qa-chips-header">
              <div className="qa-chips-label">
                <FaHeadset /> Suggested Questions
                <span className="qa-chips-count">{qaData.length}</span>
              </div>
              <button
                type="button"
                className="qa-chips-toggle"
                onClick={() => setShowSuggestions(!showSuggestions)}
                aria-label={showSuggestions ? "Collapse questions" : "Expand questions"}
              >
                {showSuggestions ? "Hide" : "Show All"}
              </button>
            </div>

            {showSuggestions && (
              <div className="qa-chips-list">
                {qaData.map((item) => (
                  <button
                    key={item.id}
                    className="qa-chip-btn"
                    onClick={() => handleChipClick(item)}
                    type="button"
                  >
                    <span className="qa-bullet">●</span>
                    <span className="qa-chip-text">{item.question}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Input Bar */}
          <form
            className="qa-input-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask a question (e.g., engine, quote, port)..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <button
              type="submit"
              className="qa-send-btn"
              disabled={!inputText.trim() || isTyping}
              aria-label="Send Message"
            >
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default QAChatbot;
